import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, downloadFile } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusBadge, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, Loader2, Check, FileDown } from "lucide-react";

export default function Quotations() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer_name: "", subtotal: 100000, discountPct: 0 });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: quotes = [], isLoading } = useQuery({ queryKey: ["quotations"], queryFn: async () => (await api.get("/quotations")).data });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/quotations", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["quotations"] }); setOpen(false); toast.success("Quotation created"); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/quotations/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["quotations"] }); toast.success("Quotation updated"); },
  });

  const submit = () => {
    const discount = Math.round(f.subtotal * (f.discountPct / 100));
    const taxable = f.subtotal - discount;
    const tax = Math.round(taxable * 0.18);
    createMut.mutate({
      customer_name: f.customer_name, status: "draft",
      items: [{ name: "Professional Services", qty: 1, price: f.subtotal, hsn: "998314" }],
      subtotal: f.subtotal, discount, tax, total: taxable + tax,
      valid_until: new Date(Date.now() + 15 * 864e5).toISOString(),
    });
  };

  return (
    <div>
      <PageHeader title="Quotations" subtitle="Create, send and get quotes accepted" testid="quotations-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-quote-btn"><Plus className="mr-1.5 h-4 w-4" /> New Quote</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create quotation</DialogTitle>
            <DialogDescription>Enter the customer, subtotal and discount; GST and the total are computed automatically.</DialogDescription>
          </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Customer</Label><Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="quote-customer-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Subtotal (₹)</Label><Input type="number" value={f.subtotal} onChange={(e) => set("subtotal", Number(e.target.value))} data-testid="quote-subtotal-input" /></div>
                <div className="space-y-1.5"><Label>Discount %</Label><Input type="number" value={f.discountPct} onChange={(e) => set("discountPct", Number(e.target.value))} data-testid="quote-discount-input" /></div>
              </div>
              <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">GST (18%)</span><span>{money(Math.round((f.subtotal - f.subtotal * f.discountPct / 100) * 0.18))}</span></div>
                <div className="mt-1 flex justify-between font-semibold"><span>Total</span><span>{money(Math.round((f.subtotal - f.subtotal * f.discountPct / 100) * 1.18))}</span></div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={createMut.isPending || !f.customer_name} data-testid="quote-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create quote"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isLoading ? <Loading /> : quotes.length === 0 ? (
        <EmptyState icon={FileText} title="No quotations yet" description="Create a quote and send it to your customer for digital acceptance."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> New Quote</Button>} testid="quotes-empty" />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Quote #</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-medium">{q.quote_number}</TableCell>
                  <TableCell>{q.customer_name}</TableCell>
                  <TableCell className="font-medium">{money(q.total)}</TableCell>
                  <TableCell><StatusBadge value={q.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF"
                        onClick={() => downloadFile(`/quotations/${q.id}/pdf`, `quotation-${q.quote_number}.pdf`).catch(() => toast.error("Could not download PDF"))}
                        data-testid={`pdf-quote-${q.id}`}><FileDown className="h-4 w-4" /></Button>
                      {q.status !== "accepted" ? (
                        <Button size="sm" variant="outline" onClick={() => updateMut.mutate({ id: q.id, body: { status: "accepted" } })} data-testid={`accept-quote-${q.id}`}>
                          <Check className="mr-1 h-3.5 w-3.5" /> Accept
                        </Button>
                      ) : <span className="text-xs text-success">Accepted</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
